import { useMutation } from "@tanstack/react-query";
import {
  firstGraphqlError,
  graphqlErrorCode,
  RATE_LIMITED_ERROR_CODE,
} from "errors";
import { useGraphql } from "~/api-client";

const RATE_LIMITED_MESSAGE =
  "Too many attempts. Please wait a moment and try again.";

/**
 * Redemption failures normally come back in the mutation payload, but app-api
 * sheds load before the resolver runs, which can only be reported as a
 * top-level GraphQL error. Map those to something showable, since throwing the
 * ClientError as-is puts its JSON dump of the response and request on screen.
 */
export function redeemCodeError(error: unknown): Error {
  if (graphqlErrorCode(error) === RATE_LIMITED_ERROR_CODE) {
    return new Error(RATE_LIMITED_MESSAGE);
  }

  const gqlError = firstGraphqlError(error);
  if (gqlError) {
    return new Error(gqlError.message);
  }

  // Network or client-side failure, leave it alone.
  return error instanceof Error ? error : new Error(String(error));
}

export function useVoucherCode() {
  const graphql = useGraphql();

  const mutation = useMutation({
    mutationKey: ["voucher-code", "redeem"],
    mutationFn: async (code: string) => {
      const data = await graphql.redeemCode(code).catch((err: unknown) => {
        throw redeemCodeError(err);
      });

      if (data.redeemCode.error) {
        throw new Error(data.redeemCode.error.message);
      }

      return data.redeemCode.messageDetails;
    },
  });

  return {
    redeemCode: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
